import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMountEffect } from './react';

interface StorageItem<T> {
  value: T;
  expiration: number | null;
}

interface LocalStorageWrapper<T> {
  setItem: (key: string, value: T, expirationInMinutes?: number | null) => void;
  getItem: (key: string) => T | null;
  removeItem: (key: string) => void;
  clearNamespace: () => void;
  getAllKeys: () => string[];
  subscribe: (callback: (event: StorageEvent) => void) => () => void;
}

interface UseLocalStorageParams<T> {
  key: string;
  namespace?: string;
  initialValue?: T;
  expirationInMinutes?: number | null;
  clearNamespaceOnMount?: boolean;
  /**
   * Default to false as what it is now.
   * But we should enable one by one and in the end make this true by default or remove this param directly.
   */
  supportSSR?: boolean;
}

// Function to create a local storage wrapper with namespace support.
const createLocalStorageWrapper = <T>(
  namespace: string
): LocalStorageWrapper<T> => {
  // Get the full key including the namespace.
  const getFullKey = (key: string) => `${namespace}:${key}`;
  const canUseLocalStorage = () =>
    typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

  // Set an item in local storage with optional expiration.
  const setItem = (
    key: string,
    value: T,
    expirationInMinutes: number | null = null
  ): void => {
    if (!canUseLocalStorage()) return;

    try {
      const fullKey = getFullKey(key);
      const item: StorageItem<T> = {
        value,
        expiration: expirationInMinutes
          ? new Date().getTime() + expirationInMinutes * 60000
          : null,
      };
      const serializedValue = JSON.stringify(item);

      localStorage.setItem(fullKey, serializedValue);
      window.dispatchEvent(
        new CustomEvent('storage-updated', { detail: { key: fullKey, value } })
      );
    } catch (error) {
      console.error('Error setting item in localStorage', error);
    }
  };

  const getItem = (key: string): T | null => {
    if (!canUseLocalStorage()) return null;

    try {
      const fullKey = getFullKey(key);
      const serializedValue = localStorage.getItem(fullKey);

      if (serializedValue) {
        const item: StorageItem<T> = JSON.parse(serializedValue);

        // Check if the item has expired.
        if (
          item.expiration === null ||
          item.expiration > new Date().getTime()
        ) {
          return item.value;
        } else {
          // Remove it from local storage if it has expired.
          localStorage.removeItem(fullKey);
        }
      }
      return null;
    } catch (error) {
      console.error('Error getting item from localStorage', error);
      return null;
    }
  };

  const removeItem = (key: string): void => {
    if (!canUseLocalStorage()) return;

    try {
      const fullKey = getFullKey(key);

      localStorage.removeItem(fullKey);
    } catch (error) {
      console.error('Error removing item from localStorage', error);
    }
  };

  // Clear all items in local storage with the current namespace.
  const clearNamespace = (): void => {
    if (!canUseLocalStorage()) return;

    try {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith(`${namespace}:`)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error clearing namespace in localStorage', error);
    }
  };

  const getAllKeys = (): string[] => {
    if (!canUseLocalStorage()) return [];

    try {
      return Object.keys(localStorage)
        .filter((key) => key.startsWith(`${namespace}:`))
        .map((key) => key.replace(`${namespace}:`, ''));
    } catch (error) {
      console.error(
        'Error getting all keys from namespace in localStorage',
        error
      );
      return [];
    }
  };

  // Use the window storage event to subscribe to changes in local storage.
  const subscribe = (callback: (event: StorageEvent) => void): (() => void) => {
    if (!canUseLocalStorage()) {
      return () => {};
    }

    const handler = (event: StorageEvent) => {
      if (event.storageArea === window.localStorage) {
        callback(event);
      }
    };
    window.addEventListener('storage', handler);

    const customHandler = (event: CustomEvent) => {
      const eventData = {
        ...event,
        key: event.detail.key,
        value: event.detail.value,
      };
      callback(eventData as unknown as StorageEvent);
    };
    window.addEventListener('storage-updated', customHandler as EventListener);

    return () => {
      window.removeEventListener('storage', handler);
      window.removeEventListener(
        'storage-updated',
        customHandler as EventListener
      );
    };
  };

  return {
    setItem,
    getItem,
    removeItem,
    clearNamespace,
    getAllKeys,
    subscribe,
  };
};

/**
 * Custom hook to use local storage with a namespace.
 *
 * Key naming convention
 * '<concept>:<key>' e.g. 'account:upgrade-dismissed'
 *
 * @param {Object} params - Parameters for the hook.
 * @param {string} params.key - The key under which the value is stored.
 * @param {string | null} params.namespace - The namespace to avoid key collisions.
 * @param {T} [params.initialValue] - The initial value to set if none exists in local storage.
 * @param {number | null} [params.expirationInMinutes] - Optional expiration time in minutes.
 * @param {boolean} [params.clearNamespaceOnMount] - Optional flag to clear the namespace on mount.
 *
 * @returns {[T | null, (value: T) => void]} - Returns the stored value and a function to update it.
 */
const useLocalStorage = <T>({
  key,
  namespace = 'default',
  initialValue,
  expirationInMinutes = null,
  clearNamespaceOnMount = false,
  supportSSR = false,
}: UseLocalStorageParams<T>): [T | null, (value: T) => void] => {
  const storage = useMemo(
    () => createLocalStorageWrapper<T>(namespace),
    [namespace]
  );

  const [storedValue, setStoredValue] = useState<T | null>(() => {
    if (supportSSR) {
      // The initial value will be null in SSR mode
      return null;
    }

    let defaultState: T | null = initialValue ?? null;
    try {
      defaultState = storage.getItem(key) ?? defaultState;
    } catch (_) {
      // localStorage is not available in this environment
    }
    return defaultState;
  });

  useEffect(() => {
    // In SSR mode, the initial value will be null, so we need to update it in the client via useEffect
    if (supportSSR) {
      setStoredValue(storage.getItem(key) ?? initialValue ?? null);
    }
  }, [initialValue, key, storage, supportSSR]);

  // Initialize local state only once on mount.
  useMountEffect(() => {
    const storedValueOnMount = storage.getItem(key);

    // Clear namespace of all other keys
    if (clearNamespaceOnMount) {
      // console.log('Clearing namespace on mount');
      storage.clearNamespace();
      if (storedValueOnMount !== null)
        storage.setItem(key, storedValueOnMount, expirationInMinutes);
    }

    if (!storedValueOnMount && initialValue !== undefined) {
      // console.log('Setting initial value in local storage');
      storage.setItem(key, initialValue, expirationInMinutes);
      setStoredValue(initialValue);
    }
  });

  // Subscribe to local storage changes and update Zustand state if local storage changes.
  useEffect(() => {
    // Read and update value when key changes
    updateStoredValue();

    const localStorageSubscription = (event: StorageEvent) => {
      if (event.key === `${namespace}:${key}`) {
        updateStoredValue();
      }
    };

    const unsubscribeStorage = storage.subscribe(localStorageSubscription);
    return () => unsubscribeStorage();

    function updateStoredValue() {
      const newValue = storage.getItem(key);
      setStoredValue(newValue);
    }
  }, [key, namespace, storage]);

  const setValue = useCallback(
    (newValue: T) => {
      setStoredValue(newValue);
      storage.setItem(key, newValue, expirationInMinutes);
    },
    [key, expirationInMinutes, storage]
  );

  return [storedValue, setValue];
};

export { useLocalStorage };
